<template>
  <ToastDialog :title="toastTitle" :message="toastMessage" :error="toastError" />



  <div class="question-detail p-4">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento...</span>
      </div>
      <div class="loading-text">Caricamento in corso...</div>
    </div>

    <BreadcrumbElement
      :items="[{ title: 'Dashboard', view: 'DashboardView' }, { title: 'Elenco Quesiti', view: 'QuestionsView' }]"
      :currentPage="'Dettaglio Quesito'" @change-view="$emit('change-view', $event.view, $event.elementId)" />

    <h1 class="mb-4">Dettaglio Quesito</h1>

    <div class="card p-4 mb-4">
      <!-- Informazioni principali -->
      <div class="mb-3">
        <p><strong>Codice Identificativo :</strong> {{ question.id }}</p>
        <p><strong>Data ricezione:</strong> {{ formatDate(question.created_at) }}</p>
        <p><strong>Utente:</strong> {{ question.created_by?.username }}</p>
        <p><strong>Ente:</strong> {{ question.organization }}</p>
        <p><strong>Regione:</strong> {{ question.region }}</p>
        <p><strong>Legislazione:</strong> {{ getLegislationTitle(question.legislation_id) }}</p>

        <p><strong>Argomenti:</strong><br>
          <span v-if="topicsSnapshot.length">
            <span v-for="topic in topicsSnapshot" :key="topic.id">
              {{ topic.title }}<br>
            </span>
          </span>
          <span v-else>—</span>
        </p>

        <p><strong>Articoli:</strong><br>
          <span v-if="articlesSnapshot.length">
            <span v-for="art in articlesSnapshot" :key="art.id">
              {{ art.title }}<br>
            </span>
          </span>
          <span v-else>—</span>
        </p>
      </div>

      <!-- Oggetto -->
      <div class="mb-3">
        <label class="form-label"><strong>Oggetto del quesito</strong></label>
        <p class="form-control-plaintext">{{ question.object }}</p>
      </div>

      <!-- Testo del quesito -->
      <div class="mb-3">
        <label class="form-label"><strong>Quesito</strong></label>
        <div class="p-2 border rounded bg-light" style="white-space: pre-wrap;">
          {{ question.text }}
        </div>
      </div>

      <!-- Risposta -->
      <div class="mb-3" v-if="question.answer">
        <label class="form-label">Risposta</label>
        <div class="p-2 border rounded bg-light" style="white-space: pre-wrap;">
          {{ question.answer }}
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Legislazione</label>
          <div class="dropdown">
            <button class="btn btn-light border text-dark dropdown-toggle w-100" type="button"
              data-bs-toggle="dropdown">
              {{ getLegislationTitle(question.legislation_id) }}
            </button>
            <ul class="dropdown-menu p-2" style="max-height: 250px; overflow-y: auto;">
              <li v-for="leg in legislations" :key="leg.id" class="form-check">
                <input class="form-check-input" type="radio" :id="'leg-' + leg.id" :value="leg.id"
                  v-model="question.legislation_id" />
                <label class="form-check-label ms-2" :for="'leg-' + leg.id">{{ leg.title }}</label>
              </li>
            </ul>
          </div>
        </div>

        <div class="col-md-4">
          <label class="form-label">Argomenti</label>
          <div class="dropdown">
            <button class="btn btn-light border text-dark w-100 d-flex justify-content-between align-items-center"
              type="button" data-bs-toggle="dropdown">
              <span>{{ selectedTopicsLabel }}</span>
            </button>
            <ul class="dropdown-menu p-2" style="max-height: 250px; overflow-y: auto; min-width: 250px;">
              <li class="mb-2 px-2">
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-white"><i class="bi bi-search"></i></span>
                  <input type="text" class="form-control" placeholder="Cerca..." v-model="topicSearch" />
                </div>
              </li>
              <li v-for="topic in filteredTopics" :key="topic.id" class="form-check">
                <input class="form-check-input" type="checkbox" :id="'topic-' + topic.id" :value="topic.id"
                  v-model="selectedTopicsIds" />
                <label class="form-check-label ms-2" :for="'topic-' + topic.id">{{ topic.title }}</label>
              </li>
            </ul>
          </div>
          <div class="mt-2 d-flex flex-column">
            <span v-for="(topicId, index) in selectedTopicsIds" :key="index"
              class="badge bg-primary mb-1 align-self-start">
              {{ getTopicName(topicId) }}
              <button type="button" class="btn-close btn-close-white btn-sm ms-1" @click="removeTopic(index)"></button>
            </span>
          </div>
        </div>

        <div class="col-md-4">
          <label class="form-label">Articoli</label>
          <div class="dropdown">
            <button class="btn btn-light border text-dark dropdown-toggle w-100" type="button"
              data-bs-toggle="dropdown">
              {{ selectedArticlesLabel }}
            </button>
            <ul class="dropdown-menu p-2" style="max-height: 250px; overflow-y: auto;">
              <li class="mb-2 px-2">
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-white"><i class="bi bi-search"></i></span>
                  <input type="text" class="form-control" placeholder="Cerca articolo..." v-model="articleSearch" />
                </div>
              </li>
              <li v-for="art in filteredArticles" :key="art.id" class="form-check">
                <input class="form-check-input" type="checkbox" :id="'art-' + art.id" :value="art.id"
                  v-model="selectedArticlesIds" />
                <label class="form-check-label ms-2" :for="'art-' + art.id">{{ getArticleTitle(art.id) }}</label>
              </li>
            </ul>
          </div>
          <div class="mt-2 d-flex flex-column">
            <span v-for="(articleId, index) in selectedArticlesIds" :key="index"
              class="badge bg-primary mb-1 align-self-start badge-wrap">
              {{ getArticleTitle(articleId) }}
              <button type="button" class="btn-close btn-close-white btn-sm ms-1"
                @click="removeArticle(index)"></button>
            </span>
          </div>
        </div>
      </div>

      <!-- Esperto assegnato 
      <div class="mb-3">
        <label class="form-label"><strong>Esperto assegnato</strong></label> 
        <select class="form-select" v-model="question.expert">
          <option :value="null">-- Seleziona esperto --</option>
          <option v-for="exp in experts" :key="exp.id" :value="exp.id">{{ exp.name }}</option>
        </select>
      </div>-->

      <div class="mb-3">
        <label class="form-label"><strong>Esperto assegnato</strong></label> 
        <select class="form-select" v-model="question.expert_id">
          <option :value="null">-- Seleziona esperto --</option>
          <option v-for="exp in experts" :key="exp.id" :value="exp.id">
            {{ exp.name + ' ' + exp.surname }}
          </option>
        </select>
      </div>

      <!-- Pulsante ASSEGNA QUESITO -->
      <div class="text-center mt-4">
        <button class="btn btn-primary px-4" @click="assignQuestion" :disabled="!question.expert_id">Assegna Quesito</button>
      </div>

    </div>
  </div>
</template>

<script>
import ToastDialog from '../../../components/common/ToastDialog.vue';
import toastable from '../../../mixins/toastable';
import BreadcrumbElement from '../../../components/common/BreadcrumbElement.vue';

export default {
  name: 'QuestionDetailView',
  mixins: [toastable],
  components: { ToastDialog, BreadcrumbElement },
  props: ['elementId'],
  data() {
    return {
      isLoading: true,
      firstLegislationLoad: true,
      question: {},
      selectedTopicsIds: [],
      selectedArticlesIds: [],
      legislations: [],
      availableArticles: [],
      availableTopics: [],
      topicSearch: '',
      articleSearch: '',
      experts: [],
      topicsSnapshot: [],
      articlesSnapshot: [],
      toastTitle: '',
      toastMessage: '',
      toastError: false,
      experts: [],
      assigned_expert_id: null,
    };
  },
  computed: {
    selectedTopicsLabel() {
      return this.selectedTopicsIds.length === 0
        ? 'Seleziona argomenti'
        : `Selezione multipla (${this.selectedTopicsIds.length})`;
    },
    filteredTopics() {
      if (!this.topicSearch) return this.availableTopics || [];
      return (this.availableTopics || []).filter(t =>
        t.title.toLowerCase().includes(this.topicSearch.toLowerCase())
      );
    },
    selectedArticlesLabel() {
      return this.selectedArticlesIds.length === 0
        ? 'Seleziona articoli'
        : `Selezione multipla (${this.selectedArticlesIds.length})`;
    },
    filteredArticles() {
      if (!this.articleSearch) return this.availableArticles || [];
      return (this.availableArticles || []).filter(a =>
        a.title.toLowerCase().includes(this.articleSearch.toLowerCase())
      );
    },
    selectedTopicsData() {
      return this.availableTopics.filter(t => this.selectedTopicsIds.includes(t.id));
    },
    selectedArticlesData() {
      return this.availableArticles.filter(a => this.selectedArticlesIds.includes(a.id));
    }
  },
  methods: {
    async loadArticlesForLegislation(legislationId) {
      if (!legislationId) {
        this.availableArticles = [];
        return;
      }

      try {
        const response = await this.$root.$api.get('/articles/bylegislation', {
          params: { legislation_id: legislationId }
        });
        this.availableArticles = response.data.map(a => ({
          ...a,
          title: a.versions?.[a.versions.length - 1]?.title || ''
        }));
      } catch (error) {
        this.showErrorToast('Errore', 'Impossibile caricare articoli per la legislazione selezionata');
      }
    },
    async loadQuestion() {
      const response = await this.$root.$api.get(`/questions/${this.elementId}`);
      const q = response.data;

      this.selectedTopicsIds = q.topics?.map(t => t.id) || [];
      this.selectedArticlesIds = q.articles?.map(a => a.id) || [];
      this.question = q;

      this.question.expert_id = q.expert?.id ?? null;

      this.topicsSnapshot = q.topics?.map(t => ({ ...t })) || [];
      this.articlesSnapshot = q.articles?.map(a => ({ ...a })) || [];
    },
    async loadDropdowns() {
      try {
        const [topicsRes, legisRes, expertsRes] = await Promise.all([
          this.$root.$api.get('/questions/topics'),
          //this.$root.$api.get('/questions/legislations'),
          Object.values(this.$root.dlgs || {}),
          //this.$root.$api.get('/experts')
        ]);

        this.availableTopics = topicsRes.data;
        this.legislations = Object.values(this.$root.dlgs || {});
        //this.experts = expertsRes.data;

        this.availableArticles = this.question.articles || [];
      } catch (error) {
        console.error('Errore nel caricamento dei dropdowns:', error);
        this.$toast.error('Errore nel caricamento dei dati di selezione');
      }
    },
    getLegislationTitle(id) {
      const leg = this.legislations.find(l => l.id === id);
      return leg ? leg.title : '—';
    },
    getArticleTitle(id) {
      if (!this.availableArticles || this.availableArticles.length === 0) return `Articolo ${id}`;
      const art = this.availableArticles.find(a => a.id === id);
      return art && art.title ? art.title : `Articolo ${id}`;
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return date.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
    },
    async assignQuestion() {
      try {
        this.question.articles = this.selectedArticlesData;
        this.question.topics = this.selectedTopicsData;
        this.question.state_id = 'ASSEGNATO'
        const payload = JSON.parse(JSON.stringify(this.question));

        payload.topics = this.selectedTopicsData.map(t => t.id);
        payload.articles = this.selectedArticlesData.map(a => a.id);

        await this.$root.$api.put(`/questions/${this.question.id}`, payload);

        const payloadAssign = {
          question_id: this.question.id,       
          legal_expert_user_id: this.question.expert_id
          //notes: this.notes || null            
        };

        await this.$root.$api.post(`/questions/${this.question.id}/assign`, payloadAssign);

        this.showSuccessToast('Complimenti', 'Quesito creato con successo');
        this.$router.push('/questions');
      } catch (error) {
        this.showErrorToast('Errore', 'Errore durante l\'assegnazione');
      }
    },
    async loadExperts() {
      try {
        const response = await this.$root.$api.get('/users/legal');
        this.experts = response.data;
      } catch (error) {
        this.showErrorToast('Errore', 'Impossibile caricare gli esperti legali');
      }
    },
    removeTopic(index) {
      this.selectedTopicsIds.splice(index, 1);
      this.question.topics = [...this.selectedTopicsIds];
    },
    getTopicName(id) {
      const topic = this.availableTopics.find(t => t.id === id);
      return topic ? topic.title : '';
    },
    getArticleTitle(id) {
      const art = this.availableArticles.find(a => a.id === id);
      return art ? art.versions[0].title : `Articolo ${id}`;
    },
    removeArticle(index) {
      this.selectedArticlesIds.splice(index, 1);
      this.question.articles = [...this.selectedArticlesIds];
    }

  },
  watch: {
    'question.legislation_id': async function (newLegId) {
      if (this.firstLegislationLoad) {
        this.firstLegislationLoad = false;
        return;
      }
      this.selectedArticlesIds = [];
      await this.loadArticlesForLegislation(newLegId);
    }
  },
  async mounted() {

    this.isLoading = true;
    try {
      await this.loadExperts();
      await this.loadQuestion();
      await this.loadDropdowns();
      await this.loadArticlesForLegislation(this.question.legislation_id);
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }
};
</script>

<style scoped>
.question-detail {
  padding: 2rem;
}

.badge-wrap {
  white-space: normal;
  word-break: break-word;
  display: inline-block;
  max-width: 100%;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.loading-text {
  margin-top: 1rem;
  font-weight: bold;
}
</style>